import React, { useEffect, useState } from 'react';
import { ThemeProvider, Box, Typography, Paper, TextField, FormControlLabel, Switch, Button, IconButton, Select, MenuItem, List, ListItem, Collapse, Snackbar, createTheme } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { COLORS } from 'colors/color';
import colors from "colors/color";
import ExistingFormDisplay from './ExistingFormDisplay';
import axios from 'axios';
import { Add as AddIcon, Save as SaveIcon, Delete as DeleteIcon, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, DragIndicator as DragIndicatorIcon, Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
const theme = createTheme({
  palette: {
    primary: { main: '#2c3e50' },
    secondary: { main: '#000' },
    background: { default: 'rgba(0, 0, 0, 0)', paper: '#ffffff' },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '0.02em' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          padding: '10px 20px',
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});
const FieldTypes = {
  text: 'Text',
  select: 'Select',
  radio: 'Radio',
  number: 'Number',
  date: 'Date',
  checkbox: 'Checkbox',
  textarea: 'Textarea'
};



const FormCustomizer = ({ initialForm, onSave }) => {
  const [expandedField, setExpandedField] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // const [showSnackbar2, setShowSnackbar2] = useState(false);
  const [fetchedData, setFetchedData] = useState([]);
  const [adminData, setAdminData] = useState();
  const [existingForm, setExistingForm] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formId, setFormId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [language, setLanguage] = useState('EN'); // Default to English
  const navigate = useNavigate();
  const userId = getUserIdFromAccessToken();


  const { t } = useTranslation();

  const apiUrl = process.env.REACT_APP_API_URL;
  const fetchCategoriesWithSubcategories = async () => {
    try {
      const response = await fetch(`${apiUrl}authentication/categories-with-subcategories/`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Categories with subcategories:', data);

      // If using the version with metadata
      if (data.categories) {
        setCategories(data.categories);
        console.log(`Found ${data.count} categories`);
      } else {
        // If using the simple version
        setCategories(data);
      }

    } catch (err) {
      console.error('Error fetching categories:', err);
      // Handle error appropriately
    }
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        await fetchCategoriesWithSubcategories();
      } catch (err) {
        console.log(err.message);
      } finally {
        console.log(false);
      }
    };

    loadCategories();
  }, []);

  const handleCategoryChange = (categoryId) => {
    console.log('Category changed to:', categoryId);
    setSelectedCategory(categoryId);
    const category = categories.find(cat => cat.id === parseInt(categoryId));
    if (category) {
      console.log('Found category with subcategories:', category.subcategories?.length || 0);
      setSubCategories(category.subcategories || []);
    } else {
      console.log('No category found, clearing subcategories');
      setSubCategories([]);
    }
  };

  const getStaticFields = () => {
    const categoryOptions = categories.map(category => ({
      value: category.id.toString(),
      label: language === 'FR' ? (category.name_fr || category.name) : category.name
    }));

    // Always provide at least one option for subcategories to satisfy backend validation
    const subcategoryOptions = subCategories.length > 0
      ? subCategories.map(subcategory => ({
        value: subcategory.id.toString(),
        label: language === 'FR' ? (subcategory.name_fr || subcategory.name) : subcategory.name
      }))
      : [{ value: "", label: language === 'FR' ? "Veuillez d'abord sélectionner une catégorie" : "Please select a category first" }];

    return [
      { name: "firstName", type: "text", label: "Prénom", required: true },
      { name: "lastName", type: "text", label: "Nom", required: true },
      {
        name: "gender", type: "radio", label: "Genre", required: true, options: [
          { value: "male", label: "M." },
          { value: "female", label: "Mme" }]
      },
      { name: "email", type: "text", label: "E-mail", required: true },
      { name: "phone", type: "text", label: "Téléphone", required: true },
      { name: "city", type: "text", label: "Ville/Pays", required: true },
      {
        name: "CategoryProfile",
        type: "select",
        label: "Catégorie",
        required: true,
        options: categoryOptions,
        onChange: handleCategoryChange
      },
      {
        name: "SubCategoryProfile",
        type: "select",
        label: "Sous-catégorie",
        required: selectedCategory && subCategories.length > 0,
        options: subcategoryOptions,
        disabled: !selectedCategory || subCategories.length === 0
      },
      {
        name: "experienceYears", type: "select", label: "Années d'expérience", required: true, options: [
          { value: "0-2", label: "0 - 2 ans" },
          { value: "3-5", label: "3 - 5 ans" },
          { value: "5+", label: "5+ ans" }
        ]
      },
      {
        name: "educationLevel", type: "select", label: "Niveau d'éducation", required: true, options: [
          { value: "bac", label: "Bac" },
          { value: "bac+2", label: "Bac+2" },
          { value: "bac+3", label: "Bac+3" },
          { value: "bac+5", label: "Bac+5 ou plus" }
        ]
      },
      { name: "resume", type: "file", label: "CV", required: true },
      { name: "coverLetter", type: "file", label: "Lettre de motivation", required: false }
    ];
  };

  const [formData, setFormData] = useState(() => {
    const defaultData = {
      admin: userId,
      title: '',
      description: '',
      language: 'EN',
      form_structure: [],
      is_active: true
    };
    return initialForm ? { ...defaultData, ...initialForm } : defaultData;
  });



  // Update form structure when categories, subcategories, or language change
  useEffect(() => {
    if (categories.length > 0) {
      const staticFields = getStaticFields();
      console.log('Updating form structure with subcategories:', subCategories.length, 'Language:', language);
      setFormData(prevData => ({
        ...prevData,
        form_structure: staticFields
      }));
    }
  }, [categories, subCategories, selectedCategory, language]);
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const toggleEditMode = () => {
    setEditMode(!editMode);
    if (!editMode && existingForm) {
      setFormData(existingForm);
    }
  };
  const updateFormData = (updates) => {
    setFormData(prevData => ({ ...prevData, ...updates }));
  };
  const isStaticField = (fieldName) => {
    const staticFields = getStaticFields();
    return staticFields.some(field => field.name === fieldName);
  };
  function getUserIdFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      //console.log("Access token : ", decodedToken);
      const userId = decodedToken.user_id;
      // fetchAdminInfo();
      return userId;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  }
  function parseJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    //console.log("payload : ", jsonPayload);

    return JSON.parse(jsonPayload);
  }
  const addField = (fieldType) => {
    const newField = {
      type: fieldType,
      name: `field_${formData.form_structure.length}`,
      label: `New ${FieldTypes[fieldType]} field`,
      options: ['select', 'radio', 'checkbox'].includes(fieldType) ? ['Option 1'] : undefined,
      required: false
    };
    updateFormData({
      form_structure: [...formData.form_structure, newField]
    });
    setExpandedField(formData.form_structure.length);
  };

  const updateField = (index, updates) => {
    const field = formData.form_structure[index];

    // Handle category selection change
    if (field.name === 'CategoryProfile' && updates.value) {
      handleCategoryChange(updates.value);
    }

    const newStructure = formData.form_structure.map((field, i) =>
      i === index ? { ...field, ...updates } : field
    );
    updateFormData({ form_structure: newStructure });
  };

  const removeField = (index) => {
    if (isStaticField(formData.form_structure[index].name)) {
      alert("Cannot remove static fields");
      return;
    }
    const newStructure = formData.form_structure.filter((_, i) => i !== index);
    updateFormData({ form_structure: newStructure });
    if (expandedField === index) setExpandedField(null);
  };
  const handleSave = async () => {
    try {
      // ADD THIS DEBUG LOGGING
      console.log('=== DEBUG: Form Save Request ===');
      console.log('formId:', formId);
      console.log('apiUrl:', apiUrl);
      console.log('formData:', JSON.stringify(formData, null, 2));
      console.log('Token:', localStorage.getItem("accessToken"));
      console.log('Full URL:', `${apiUrl}authentication/application-forms/${formId ? formId + '/' : ''}`);

      let response;
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem("accessToken")}`
      };

      if (formId) {
        response = await axios.put(`${apiUrl}authentication/application-forms/${formId}/`, formData, { headers });
      } else {
        response = await axios.post(`${apiUrl}authentication/application-forms/`, formData, { headers });
      }

      console.log('Success response:', response.data);
      // ... rest of your success code
    } catch (error) {
      console.error('Error saving form:', error);

      // IMPROVE ERROR LOGGING
      console.log('=== ERROR DETAILS ===');
      console.log('Status:', error.response?.status);
      console.log('Status Text:', error.response?.statusText);
      console.log('Response Data:', error.response?.data);
      console.log('Request Config:', error.config);

      let errorMessage = 'An error occurred while saving the form.';
      if (error.response?.data) {
        // Handle different error response formats
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }
      showSnackbar(errorMessage, 'error');
    }
  };

  const fetchAdminInfo = async () => {
    try {
      const response = await axios.get(`${apiUrl}authentication/admins/${userId}/`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem("accessToken")}`
        }
      });
      setAdminData(response.data);
      console.log('Admin data22:', response.data);
    } catch (error) {
      console.error('Error fetching admin info:', error);
      if (error.response?.status === 401) {
        navigate('/login');  // Redirect if unauthorized
      } else {
        showSnackbar('An error occurred while fetching admin data.', 'error');
      }
    }
  };

  const handleGetApps = async () => {
    try {
      const response = await axios.get(`${apiUrl}authentication/application-forms/`, {
        params: { admin: userId },
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem("accessToken")}`
        }
      });
      const fetchedData = Array.isArray(response.data) ? response.data : [response.data];
      setFetchedData(fetchedData);
      console.log('data fetched: ', fetchedData);

      if (fetchedData.length > 0) {
        setExistingForm(fetchedData[0]);
        setFormId(fetchedData[0].id);
        setFormData(fetchedData[0]);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
      let errorMessage = 'An error occurred while fetching forms.';
      if (error.response) {
        errorMessage = error.response.data.message || errorMessage;
      }
      showSnackbar(errorMessage, 'error');
    }
  };

  useEffect(() => {
    fetchAdminInfo();
    handleGetApps();
  }, []);


  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(formData.form_structure);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    updateFormData({ form_structure: items });
  };

  const toggleFieldExpansion = (index) => {
    setExpandedField(expandedField === index ? null : index);
  };

  const addOption = (fieldIndex) => {
    const field = formData.form_structure[fieldIndex];
    const newOptions = [...(field.options || []), `Option ${(field.options || []).length + 1}`];
    updateField(fieldIndex, { options: newOptions });
  };

  const updateOption = (fieldIndex, optionIndex, newValue) => {
    const field = formData.form_structure[fieldIndex];
    const newOptions = field.options.map((opt, idx) => idx === optionIndex ? newValue : opt);
    updateField(fieldIndex, { options: newOptions });
  };

  const removeOption = (fieldIndex, optionIndex) => {
    const field = formData.form_structure[fieldIndex];
    const newOptions = field.options.filter((_, idx) => idx !== optionIndex);
    updateField(fieldIndex, { options: newOptions });
  };


  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ maxWidth: 1000, margin: 'auto', padding: 4, backgroundColor: 'background.default', minHeight: '100vh' }}>
        <Typography variant="h4" align="center" color="#1a237e" sx={{ mb: 6 }}>
          {t('spontane.title')}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={editMode}
                onChange={toggleEditMode}
                color="primary"
              />
            }
            label={editMode ? t('spontane.edit') : t('spontane.mode')}
          />
          {!editMode && existingForm && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={toggleEditMode}
              sx={{
                height: "100%",
                color: COLORS.white,
                border: "1px solid",
                borderColor: colors.primary,
                bgcolor: colors.primary,
                ":hover": {
                  bgcolor: COLORS.white,
                  borderColor: colors.primary,
                  color: colors.primary,
                },
              }}
            >
              {t('spontane.edit')}
            </Button>
          )}
          {editMode && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{
                height: "100%",
                color: COLORS.white,
                border: "1px solid",
                borderColor: colors.primary,
                bgcolor: colors.primary,
                ":hover": {
                  bgcolor: COLORS.white,
                  borderColor: colors.primary,
                  color: colors.primary,
                },
              }}
            >
              {formId ? t('spontane.update') : t('spontane.save')}
            </Button>
          )}
        </Box>

        {existingForm && !editMode ? (
          <ExistingFormDisplay formData={existingForm} />
        ) : (
          <>
            <Paper elevation={0} sx={{ padding: 4, mb: 4, borderRadius: 4, background: 'linear-gradient(145deg, #ffffff, #f0f0f0)' }}>
              <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
                <TextField
                  fullWidth
                  label={t('formCustomizer.formTitle')}
                  value={formData.title}
                  onChange={(e) => updateFormData({ title: e.target.value })}
                  variant="outlined"
                  sx={{ flex: 2 }}
                  disabled={!editMode}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => updateFormData({ is_active: e.target.checked })}
                      color="secondary"
                      disabled={!editMode}
                    />
                  }
                    label={formData.is_active ? 'actif' : 'désactivé'}
                  sx={{ flex: 1 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={language === 'FR'}
                      onChange={(e) => {
                        const newLanguage = e.target.checked ? 'FR' : 'EN';
                        setLanguage(newLanguage);
                        updateFormData({ language: newLanguage });
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1976d2', // Color when checked (e.g., blue)
                        },
                        '& .MuiSwitch-switchBase': {
                          color: '#d32f2f', // Color when unchecked (e.g., red)
                        },
                        '& .Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1976d2',
                        },
                        '& + .MuiSwitch-track': {
                          backgroundColor: '#d32f2f',
                        },
                      }}
                    />

                  }
                  label={`${language}`}
                  sx={{ flex: 1 }}
                />
              </Box>
              <TextField
                fullWidth
                label={t('formCustomizer.formDescription')}
                value={formData.description}
                onChange={(e) => updateFormData({ description: e.target.value })}
                variant="outlined"
                multiline
                rows={3}
                sx={{ mb: 4 }}
                disabled={!editMode}
              />
              {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('formCustomizer.recruiterID')}: {formData.recruiter}
              </Typography> */}
              {formData.created_at && (
                <Typography variant="body2" color="text.secondary">
                  {t('formCustomizer.created')} {new Date(formData.created_at).toLocaleString()}
                </Typography>
              )}
              {formData.updated_at && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  {t('formCustomizer.lastUpdated')} {new Date(formData.updated_at).toLocaleString()}
                </Typography>
              )}
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="fields">
                  {(provided) => (
                    <List {...provided.droppableProps} ref={provided.innerRef} sx={{ mb: 4 }}>
                      {formData.form_structure.map((field, index) => (
                        <Draggable
                          key={field.name}
                          draggableId={field.name}
                          index={index}
                          isDragDisabled={!editMode}
                        >
                          {(provided) => (
                            <ListItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                mb: 2,
                                borderRadius: 2,
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                backgroundColor: theme.palette.background.paper,
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: editMode && !isStaticField(field.name) ? 'translateY(-5px)' : 'none',
                                  boxShadow: editMode && !isStaticField(field.name) ? '0 6px 12px rgba(0,0,0,0.15)' : '0 4px 6px rgba(0,0,0,0.1)',
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', p: 2 }}>
                                {editMode && (
                                  <DragIndicatorIcon sx={{ mr: 2, color: 'text.secondary' }} />
                                )}
                                <TextField
                                  fullWidth
                                  value={field.label}
                                  onChange={(e) => updateField(index, { label: e.target.value })}
                                  variant="outlined"
                                  size="small"
                                  sx={{ mr: 2 }}
                                  disabled={!editMode}
                                />
                                <Select
                                  value={field.type}
                                  onChange={(e) => updateField(index, { type: e.target.value })}
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    width: 150,
                                    mr: 2,
                                    display: isStaticField(field.name) ? 'none' : 'block'
                                  }}
                                  disabled={!editMode || isStaticField(field.name)}
                                >
                                  {Object.entries(FieldTypes).map(([type, label]) => (
                                    <MenuItem key={type} value={type}>{label}</MenuItem>
                                  ))}
                                </Select>

                                {/* Only show delete button for non-static fields */}
                                {editMode && !isStaticField(field.name) && (
                                  <IconButton
                                    onClick={() => removeField(index)}
                                    color="secondary"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}

                                {/* Only show expand button for non-static fields */}
                                {editMode && !isStaticField(field.name) && (
                                  <IconButton
                                    onClick={() => toggleFieldExpansion(index)}
                                  >
                                    {expandedField === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </IconButton>
                                )}
                              </Box>
                              <Collapse in={expandedField === index} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2 }}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={field.required}
                                        onChange={(e) => updateField(index, { required: e.target.checked })}
                                        disabled={!editMode || isStaticField(field.name)}
                                      />
                                    }
                                    label={t('formCustomizer.required')}
                                  />
                                  {['select', 'radio', 'checkbox'].includes(field.type) && (
                                    <Box sx={{ mt: 2 }}>
                                      <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('formCustomizer.options')}</Typography>
                                      {field.options?.map((option, optionIndex) => (
                                        <Box key={optionIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                          <TextField
                                            value={option}
                                            onChange={(e) => updateOption(index, optionIndex, e.target.value)}
                                            size="small"
                                            fullWidth
                                            disabled={!editMode || isStaticField(field.name)}
                                          />
                                          {editMode && !isStaticField(field.name) && (
                                            <IconButton
                                              onClick={() => removeOption(index, optionIndex)}
                                              disabled={field.options.length <= 1}
                                              size="small"
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          )}
                                        </Box>
                                      ))}
                                      {editMode && !isStaticField(field.name) && (
                                        <Button
                                          startIcon={<AddIcon />}
                                          onClick={() => addOption(index)}
                                          size="small"
                                          sx={{ mt: 1 }}
                                        >
                                          {t('formCustomizer.addOption')}
                                        </Button>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </Collapse>
                            </ListItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            </Paper>
            {editMode && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => addField('text')}
                  sx={{
                    height: "100%",
                    color: colors.primary,
                    border: "1px solid",
                    borderColor: COLORS.grey,
                    bgcolor: COLORS.Button_OnHold,
                    ":hover": {
                      bgcolor: COLORS.white,
                      borderColor: colors.primary,
                      color: colors.primary,
                    },
                  }}
                >
                  {t('spontane.addField')}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  sx={{
                    height: "100%",
                    color: COLORS.white,
                    border: "1px solid",
                    borderColor: colors.primary,
                    bgcolor: colors.primary,
                    ":hover": {
                      bgcolor: COLORS.white,
                      borderColor: colors.primary,
                      color: colors.primary,
                    },
                  }}
                >
                  {formId ? t('spontane.update') : t('spontane.save')}
                </Button>
              </Box>
            )}
          </>
        )}

        {adminData && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => window.open(`/candidature-spontanee/${adminData.company_details.name}`)}
            sx={{
              mt: 4,
              height: "100%",
              color: COLORS.white,
              border: "1px solid",
              borderColor: colors.primary,
              bgcolor: colors.primary,
              ":hover": {
                bgcolor: COLORS.white,
                borderColor: colors.primary,
                color: colors.primary,
              },
            }}
          >
            {t('spontane.viewApplicationPage')}
          </Button>
        )}
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        message={snackbar.message}
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            color: "black",
            borderRadius: "50px",
            backdropFilter: "blur(10px)",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
          },
        }}
      />
    </ThemeProvider>
  );
};

export default FormCustomizer;